import { ArchiveBoxXMarkIcon } from '@heroicons/react/24/outline';
import Modal from 'antd/es/modal/Modal';
import { useCallback, useState } from 'react';

import { useOpenNotification } from 'utils/antd/notification-toasts';

import type { PayoutRequest } from 'api';
import { useDeleteDisbursementPayoutRequestsUniqueReference } from 'api';

import { useOrganisationId } from 'hooks/context';

import { PrimaryButton } from '@atoms/buttons/PrimaryButton';

import { CANCEL_PAYOUT_BUTTON_TEST_ID } from './constants';

type CancelPayoutProps = {
  payoutDetails: PayoutRequest;
};

export const CancelPayout = ({ payoutDetails }: CancelPayoutProps) => {
  const { mutate } = useDeleteDisbursementPayoutRequestsUniqueReference();
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const organisationId = useOrganisationId<number>();

  const toast = useOpenNotification();

  const onCancelPayoutRequest = useCallback(() => {
    if (payoutDetails === undefined) {
      return;
    }

    mutate(
      {
        organisationId,
        uniqueReference: payoutDetails.unique_reference
      },
      {
        onSuccess(response) {
          if (response.data.status === 'pending') {
            toast({
              kind: 'success',
              message: 'Payout cancellation process initaited'
            });
          } else {
            toast({
              kind: 'warning',
              message: 'Unable to cancel Payout'
            });
          }
        },
        onError() {
          toast({
            kind: 'error',
            message: 'Cancellation of Payout failed'
          });
        },
        onSettled() {
          setModalOpen(false);
        }
      }
    );
  }, [payoutDetails, mutate, organisationId, toast]);

  if (payoutDetails === undefined) {
    return null;
  }

  return (
    <div>
      <PrimaryButton
        danger
        type="default"
        className="my-2 flex w-full items-center justify-center gap-1"
        onClick={() => setModalOpen(true)}
        data-testid={CANCEL_PAYOUT_BUTTON_TEST_ID}
      >
        <ArchiveBoxXMarkIcon className="size-5" />
        Cancel payout
      </PrimaryButton>
      <Modal
        title="Cancel payout request?"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
      >
        <div className="mb-4 text-balance">
          We&rsquo;ll do our best, but cancellation may not be possible once
          processing has started.
        </div>
        <div className="grid w-full grid-cols-2 gap-4">
          <PrimaryButton onClick={() => setModalOpen(false)}>
            Keep it
          </PrimaryButton>
          <PrimaryButton type="default" danger onClick={onCancelPayoutRequest}>
            Cancel request
          </PrimaryButton>
        </div>
      </Modal>
    </div>
  );
};
